import { type Ref } from "vue";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import Unipile from "@/services/unipile/unipile";
import type { SendWhatsAppMessageParams } from "./types";
import type { Message } from "@/dtos/Unipile.dto";

interface UseSendWhatsAppMessageOptions {
  messages?: Ref<Message[]>;
}

export function useSendWhatsAppMessage(options: UseSendWhatsAppMessageOptions = {}) {
  const { messages } = options;
  const queryClient = useQueryClient();

  const sendMessageMutation = useMutation({
    mutationFn: async (params: SendWhatsAppMessageParams) => {
      const requestPayload: any = {};

      if (params.text) {
        requestPayload.text = params.text;
      }

      if (params.quote_id) {
        requestPayload.quote_id = params.quote_id;
      }

      if (params.attachments && params.attachments.length > 0) {
        const validFiles = params.attachments.filter((file) => file instanceof File);
        if (validFiles.length > 0) {
          requestPayload.attachments = validFiles;
        }
      }

      if (params.voice_message) {
        requestPayload.voice_message = params.voice_message;
      }

      if (params.video_message) {
        requestPayload.video_message = params.video_message;
      }

      const response = await Unipile.sendMessage(params.conversationId, requestPayload);

      if (!response.data?.status) {
        const errorData = response.data as any;
        throw new Error(errorData?.message || errorData?.msg || "Failed to send message");
      }

      return response.data;
    },
    onMutate: async (variables) => {
      if (!messages) return {};

      const previousMessages = [...messages.value];

      const tempMessage: Message = {
        id: `temp-${Date.now()}`,
        conversationId: variables.conversationId,
        text: variables.text || "",
        content: variables.text || "",
        senderName: "You",
        isOwn: true,
        isSender: true,
        isPending: true,
        channel: "whatsapp",
        timestamp: new Date().toISOString(),
        readReceipts: [],
        readCount: 0,
        attachments: [],
      } as any;

      messages.value = [...messages.value, tempMessage];

      return { previousMessages };
    },
    onError: (_error, _variables, context) => {
      if (messages && context?.previousMessages) {
        messages.value = context.previousMessages;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["whatsapp-conversations"] });
    },
  });

  return {
    sendMessage: sendMessageMutation.mutateAsync,
    isSending: sendMessageMutation.isPending,
    sendError: sendMessageMutation.error,
  };
}
